/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import { useContext, useMemo } from 'react';

import { CORNER_THICKNESS, CORNER_WIDTH, ReferenceMap } from '@rta/rta-blueprint-library';

import { AppContext } from '@r-app/app-context';
import { convertRect2Vectors } from '@r-core/helpers';
import { CreateCornerPattern } from '../symbols';
import { DisplayMode } from '@r-core';
import { svgPolygonFromVectors } from '../helpers/svg-polygon-from-vectors';
import { Layer } from '@r-core/types';
import { renderTracking } from '@r-services/render-tracking';
import { useFps } from '@r-hooks';

import '../../app/styles/blueprint-component.scss';

export type RenderPanelShadowProps = {
	islandId: string;
	panelId: string;
};

export const RenderPanelShadow = (props: RenderPanelShadowProps): React.ReactElement => {
	const { controller: { options, navigation, kitchen }} = useContext(AppContext);
	const {
		panelId
	} = props;

	renderTracking.incrementRender('RenderPanelShadow');

	const referenceMap = useMemo<ReferenceMap>(() => {
		// console.log('usePanel:useMemo:getReferenceMap', panelId);
		return kitchen.getReferenceMap(panelId);
	}, [panelId]);

	const { island, panel } = referenceMap;

	useFps(`show-panel-${island?.sku.toLowerCase() ?? 'na'}-${panel.label?.toLowerCase() ?? 'na'}`, options.fps);

	/* get countertop details from panel perspective */
	let sidePoints = options.showCountertops ? panel.getSidePoints({ includeCountertop: true, translate: false, rotate: false }) : null;
	const countertop = island.getCountertop();
	const countertopCenter = { x: sidePoints ? sidePoints.center.x : 0, y: countertop.center.y };
	const countertopSize = {
		// calculate width using sidepoints
		width: sidePoints ? sidePoints.corners[1].x - sidePoints.corners[0].x : 0,
		// we only use width and depth for panel view
		height: countertop.size.height,
		// countertop thickness is the same accross all panel views
		depth: countertop.size.depth
	};
	// console.log('countertop', { size: countertopSize, center: countertopCenter, corners: sidePoints?.corners });

	// panel sides, don't need to execute again if countertop is not showing
	if (options.showCountertops) sidePoints = panel.getSidePoints({ includeCountertop: false, translate: false, rotate: false });
	const panelSize = {
		width: sidePoints ? sidePoints.corners[1].x - sidePoints.corners[0].x : 0,
		height: panel.size.height,
		depth: panel.size.depth
	};
	// console.log('panel', { label: panel.label, size: panelSize, center: sidePoints?.center, corners: sidePoints?.corners });

	// surround
	const materialAsset = island.panels.surroundMaterial;
	const materialScale = materialAsset.scale;

	const cornerOffsetX = (panelSize.width / 2) + (CORNER_THICKNESS) - (CORNER_WIDTH / 2);

	let showTexture = true;
	let { showCountertops, showCorners } = options;

	let shadowOpacity = 0.9;

	let overlapFill = 'darkgray';
	switch (navigation.displayMode) {
		case DisplayMode.Manufacturing:
		case DisplayMode.InlineManufacturing:
			showTexture = false;
			showCountertops = false;
			shadowOpacity = 0.5;
			showCorners = true;
			overlapFill = 'gray';
			showCountertops = true;
			break;
		default:
	}

	const vectors = {
		panel: convertRect2Vectors({
			layer: Layer.Panel,
			center: { x: 0, y: panel.center.y },
			width: panelSize.width,
			height: panelSize.height,
			angle: 0 // panel.getOriginAngle()
		}),
		countertop: convertRect2Vectors({
			layer: Layer.Countertop,
			center: { x: countertopCenter.x, y: countertopCenter.y },
			width: countertopSize.width,
			height: countertopSize.depth,
			angle: 0 // panel.getOriginAngle()
		}),
		leftCorner: convertRect2Vectors({
			layer: Layer.Corner,
			center: { x: -cornerOffsetX, y: panel.center.y },
			width: CORNER_WIDTH,
			height: panelSize.height,
			angle: 0 // panel.getOriginAngle()
		}),
		rightCorner: convertRect2Vectors({
			layer: Layer.Corner,
			center: { x: cornerOffsetX, y: panel.center.y },
			width: CORNER_WIDTH,
			height: panelSize.height,
			angle: 0 // panel.getOriginAngle()
		})
	}

	// console.log('showpanels-vectors', vectors);

	return (<g id={`panel-${panel.id}`} >
		{ /* create corner pattern def */}
		{ showCorners && <CreateCornerPattern {...{ material: kitchen.material, options }} />}

		{ /* create surround pattern def */}
		{ showTexture && <defs>
			<pattern id="surround-texture" x="0" y="0" height={materialScale.height} width={materialScale.width} patternUnits="userSpaceOnUse">
				<image href={`${options.assetsBaseUrl}/textures/${materialAsset.texture}${options.assetsSasToken}`} x="0" y="0" width={materialScale.width} height={materialScale.height} />
			</pattern>
		</defs>}

		{ /* render countertop with texture */ }
		{ showCountertops &&
			svgPolygonFromVectors(vectors.countertop, {
				type: 'panel-countertop',
				fill: `url(#counter-texture-${countertop.id})`
			})
		}

		{ /* shade the countertop */ }
		{ showCountertops &&
			svgPolygonFromVectors(vectors.countertop, {
				type: 'panel-countertop-shadow',
				fill: overlapFill,
				stroke: 'none',
				strokeWidth: '.08',
				opacity: 0.7
			})
		}

		{ /* no texture required, draw blank panel */ }
		{/* { (!showTexture) && drawSvgPolygonFromVectors(vectors.panel, {
				key: 'panel-context',
				fill: 'darkgray',
				opacity: shadowOpacity
			})
		} */}

		{ /* draw shadow panel */ }
		{ /* draw shadow panel texture */
		svgPolygonFromVectors(vectors.panel, {
			key: `panel-shadow-texture-${panel.id}`,
			fill: 'url(#surround-texture)',
			opacity: shadowOpacity
		})}
		{ /* draw shadow panel overlay */
		svgPolygonFromVectors(vectors.panel, {
			key: `panel-shadow-overlay-${panel.id}`,
			fill: overlapFill,
			opacity: shadowOpacity
		})}

		{ showCorners && options.corner.show && <>
			{ /* left corner */
			svgPolygonFromVectors(vectors.leftCorner, {
				key: 'panel-corner-left',
				fill: overlapFill,
				opacity: shadowOpacity
			})}
			{ /* right corner */
			svgPolygonFromVectors(vectors.rightCorner, {
				key: 'panel-corner-right',
				fill: overlapFill,
				opacity: shadowOpacity
			})}
		</>}

		{ /* Developer Layer / Top */ }
		{ options.showDeveloperInsights && <>
		</>}
	</g>);
};
